import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  UpgradeLoggerRepository,
  UpgradeSession,
  UpgradeEvent,
  SessionStatistics,
  PhaseProgress
} from 'ngx-core-services';

import {
  SessionSummary,
  SessionDetailsView
} from '../upgrade-logs-viewer.model';

@Injectable({
  providedIn: 'root'
})
export class UpgradeLogsViewerService {

  constructor(
    private upgradeLoggerRepository: UpgradeLoggerRepository
  ) {}

  // Search methods
  searchDeviceUpgrades(deviceSerial: string, limit = 20): Observable<SessionSummary[]> {
    if (!deviceSerial?.trim()) {
      return new Observable(observer => observer.next([]));
    }

    return this.upgradeLoggerRepository.getDeviceUpgrades(deviceSerial, limit).pipe(
      map(sessions => sessions.map(session => this.mapToSessionSummary(session)))
    );
  }

  searchUpgradesByDateRange(from: Date, to: Date): Observable<SessionSummary[]> {
    // Implementation would use collectionGroup query with date range
    // For now, return empty array as this requires more complex Firestore queries
    return new Observable(observer => observer.next([]));
  }

  searchUpgradesByOperator(operatorId: string): Observable<SessionSummary[]> {
    return this.upgradeLoggerRepository.getOperatorUpgrades(operatorId).pipe(
      map(sessions => sessions.map(session => this.mapToSessionSummary(session)))
    );
  }

  // Detail methods
  getSessionDetails(deviceSerial: string, sessionId: string, sessionSummary?: SessionSummary): Observable<SessionDetailsView> {
    // Get the real session from repository and events in parallel
    return this.upgradeLoggerRepository.getSession(deviceSerial, sessionId).pipe(
      switchMap(realSession => {
        // Use real session if available, otherwise construct from sessionSummary
        const session = this.buildSessionFromData(sessionId, deviceSerial, realSession, sessionSummary);

        // Get events for this session
        return this.upgradeLoggerRepository.getSessionEvents(deviceSerial, sessionId).pipe(
          map(events => {
            const sgpCommands = this.extractSgpCommands(events);
            const phases = this.extractPhaseProgress(events);
            const statistics = this.calculateSessionStatistics(session, events);

            return {
              session,
              events,
              sgpCommands,
              phases,
              deviceSnapshot: session.preUpgradeSnapshot,
              statistics
            };
          })
        );
      })
    );
  }

  private buildSessionFromData(
    sessionId: string,
    deviceSerial: string,
    realSession: UpgradeSession | null,
    sessionSummary?: SessionSummary
  ): UpgradeSession {
    // If we have the real session, use it directly
    if (realSession) {
      // Ensure endTime fallback to lastUpdated if not available
      return {
        ...realSession,
        endTime: realSession.endTime || realSession.lastUpdated
      };
    }

    // Fallback: construct session from sessionSummary
    const currentTime = Date.now();
    const startTime = sessionSummary?.startTime || currentTime;
    const endTime = sessionSummary?.endTime || sessionSummary?.startTime; // Use startTime as fallback for endTime

    return {
      sessionId,
      deviceSerial,
      deviceModel: sessionSummary?.deviceModel || 'Unknown',
      operatorId: 'unknown',
      operatorName: sessionSummary?.operatorName || 'Unknown',
      operatorEmail: 'unknown@example.com',
      status: sessionSummary?.status || 'completed',
      startTime,
      endTime,
      lastUpdated: currentTime,
      epochStart: startTime,
      modules: sessionSummary?.modules?.map(m => ({ name: m, status: 'pending' as any })) || [],
      preUpgradeSnapshot: {
        timestamp: startTime,
        inverterStatus: {},
        batteryDetails: [],
        captureMethod: 'pre_upgrade' as const
      },
      fwItemsTarget: [] // Default empty array for fallback sessions
    };
  }

  getSessionEvents(deviceSerial: string, sessionId: string): Observable<UpgradeEvent[]> {
    return this.upgradeLoggerRepository.getSessionEvents(deviceSerial, sessionId);
  }

  getSgpCommands(deviceSerial: string, sessionId: string): Observable<UpgradeEvent[]> {
    return this.getSessionEvents(deviceSerial, sessionId).pipe(
      map(events => this.extractSgpCommands(events))
    );
  }

  // Analytics methods
  calculateSessionStatistics(session: UpgradeSession, events: UpgradeEvent[]): SessionStatistics {
    const sgpCommands = this.extractSgpCommands(events);
    const errors = events.filter(event => event.operation === 'error');

    const totalCommandTime = sgpCommands.reduce((total, cmd) => {
      return total + (cmd.sgpCommandDetails?.executionTimeMs || 0);
    }, 0);

    // Calculate phase durations
    const phaseDurations: { [phase: string]: number } = {};
    const phases = this.extractPhaseProgress(events);
    phases.forEach(phase => {
      if (phase.duration) {
        phaseDurations[phase.phase] = phase.duration;
      }
    });

    return {
      totalEvents: events.length,
      totalSgpCommands: sgpCommands.length,
      totalErrors: errors.length,
      phaseDurations
    };
  }

  extractPhaseProgress(events: UpgradeEvent[]): PhaseProgress[] {
    const phases: { [key: string]: PhaseProgress } = {};

    events.forEach(event => {
      if (event.phase && event.moduleId) {
        const key = `${event.moduleId}_${event.phase}`;

        if (!phases[key]) {
          phases[key] = {
            phase: event.phase,
            startTime: event.timestamp,
            progress: event.progress || 0,
            moduleId: event.moduleId
          };
        } else {
          phases[key].endTime = event.timestamp;
          phases[key].progress = Math.max(phases[key].progress, event.progress || 0);
        }
      }
    });

    // Calculate durations
    Object.values(phases).forEach(phase => {
      if (phase.endTime) {
        phase.duration = phase.endTime - phase.startTime;
      }
    });

    return Object.values(phases);
  }

  // Export methods
  async exportSessionToJson(deviceSerial: string, sessionId: string): Promise<Blob> {
    const sessionDetails = await this.getSessionDetails(deviceSerial, sessionId).toPromise();
    const jsonString = JSON.stringify(sessionDetails, null, 2);
    return new Blob([jsonString], { type: 'application/json' });
  }

  async exportSgpCommandsToText(deviceSerial: string, sessionId: string): Promise<Blob> {
    const sgpCommands = await this.getSgpCommands(deviceSerial, sessionId).toPromise();

    const textContent = sgpCommands.map(cmd => {
      const details = cmd.sgpCommandDetails;
      if (!details) return '';

      return `Command: ${details.commandCode}\n` +
             `Raw Command: ${details.rawCommand}\n` +
             `Raw Response: ${details.rawResponse || 'N/A'}\n` +
             `Success: ${details.success}\n` +
             `Timestamp: ${new Date(cmd.timestamp).toISOString()}\n` +
             `---\n`;
    }).join('\n');

    return new Blob([textContent], { type: 'text/plain' });
  }

  // Private helper methods
  private mapToSessionSummary(session: UpgradeSession): SessionSummary {
    // Calculate effective end time: use endTime if available, otherwise lastUpdated
    const effectiveEndTime = session.endTime || session.lastUpdated;
    const duration = effectiveEndTime - session.startTime;

    // Extract module names from session.modules array correctly
    const modules = this.extractModuleNames(session.modules);

    return {
      sessionId: session.sessionId,
      deviceSerial: session.deviceSerial,
      deviceModel: session.deviceModel || 'INVERTER',
      operatorName: session.operatorName || 'Unknown',
      startTime: session.startTime,
      endTime: session.endTime, // Keep original endTime (can be undefined)
      status: session.status,
      modules,
      duration,
      errorCount: 0, // Would need to count from events
      sgpCommandCount: 0 // Would need to count from events
    };
  }

  private extractModuleNames(modules: any[]): string[] {
    if (!modules?.length) return ['ARM', 'INVERTER'];

    return modules.map(m => {
      if (typeof m === 'object' && m.name) {
        return m.name; // Extract name from object like {name: "ARM", status: "pending"}
      }
      return m.toString(); // Fallback for string modules
    });
  }

  private extractSgpCommands(events: UpgradeEvent[]): UpgradeEvent[] {
    return events.filter(event =>
      event.operation === 'sgp_command' &&
      event.sgpCommandDetails?.rawCommand
    );
  }
}
